import React, { useEffect, useState } from "react";
import HtmlContent from "./HtmlContent";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { fetchPosts } from "../../redux/postSlice";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  IconButton,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";

const PostList: React.FC = () => {
  const { loading, posts } = useAppSelector((state) => state.posts);
  const dispatch = useAppDispatch();
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [viewMode, setViewMode] = useState<"list" | "view" | "purchase">(
    "list"
  );

  useEffect(() => {
    dispatch(fetchPosts());
  }, [dispatch]);

  const handleViewPost = (post: any) => {
    setSelectedPost(post);
    setViewMode("view");
  };

  const handleBuyPost = (post: any) => {
    setSelectedPost(post);
    setViewMode("purchase");
    setActiveStep(0);
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedPost(null);
    setActiveStep(0);
  };

  const handlePurchase = async () => {
    // Simulate purchase process
    setActiveStep(1);

    // Simulate API call
    setTimeout(() => {
      setActiveStep(2);
    }, 2000);
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );

  const NoPostsIllustration = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
        textAlign: "center",
      }}
    >
      <Box
        component="img"
        src="/images/news.svg"
        alt="No news available"
        sx={{
          width: { xs: 420, sm: 450 },
          height: "auto",
          mb: 3,
          opacity: 0.7,
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: "text.secondary",
          mb: 1,
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
        }}
      >
        No News Yet
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          maxWidth: "300px",
          lineHeight: 1.5,
        }}
      >
        Check back later for the latest updates and announcements.
      </Typography>
    </Box>
  );

  // Filter only published posts for public view
  const publishedPosts = posts.filter((post) => post.isPublished);

  // Full Article View
  if (viewMode === "view" && selectedPost) {
    return (
      <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ backgroundColor: "background.paper" }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={handleBackToList} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" component="div" noWrap>
              {selectedPost.title}
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2 }}>
          <Box display="flex" gap={2} alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              By {selectedPost.creatorUserName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(selectedPost.creationTime)}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <HtmlContent
            content={selectedPost.content}
            className="post-content-full"
          />
        </Box>
      </Box>
    );
  }

  // Purchase Flow View
  if (viewMode === "purchase" && selectedPost) {
    return (
      <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
        <AppBar
          position="sticky"
          color="default"
          elevation={1}
          sx={{ backgroundColor: "background.paper" }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={handleBackToList} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" component="div">
              Purchase Article
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            <Step>
              <StepLabel>Article Details</StepLabel>
              <StepContent>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {selectedPost.title}
                  </Typography>
                  {selectedPost.shortVersion && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {selectedPost.shortVersion}
                    </Typography>
                  )}
                  <Typography variant="body1" gutterBottom>
                    Price:{" "}
                    <strong>
                      {formatPrice(
                        selectedPost.price || 0,
                        selectedPost.currency || "USD"
                      )}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Author: {selectedPost.creatorUserName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Published: {formatDate(selectedPost.creationTime)}
                  </Typography>

                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handlePurchase}
                      startIcon={<MoneyIcon />}
                      color="secondary"
                      fullWidth
                    >
                      Purchase Now
                    </Button>
                    <Button onClick={handleBackToList} sx={{ mt: 1 }} fullWidth>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Processing Payment</StepLabel>
              <StepContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={20} />
                  <Typography>Completing your purchase...</Typography>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>Purchase Complete</StepLabel>
              <StepContent>
                <Typography>
                  Thank you for your purchase! You now have access to the full
                  content.
                </Typography>
                {selectedPost && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "success.light",
                      borderRadius: 1,
                    }}
                  >
                    <HtmlContent
                      content={selectedPost.content}
                      className="post-content-full"
                    />
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={handleBackToList}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  Back to Articles
                </Button>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </Box>
    );
  }

  // Main List View
  return (
    <Box className="post-list" sx={{ maxWidth: 800, margin: "0 auto", px: 2 }}>
      {publishedPosts.map((post) => (
        <Card
          key={post.id}
          className="post-item"
          sx={{
            mb: 3,
            border: post.isForSale ? "2px solid" : "1px solid",
            borderColor: post.isForSale ? "secondary.main" : "divider",
            boxShadow: post.isForSale ? 3 : 1,
          }}
        >
          <CardContent sx={{ pb: 1 }}>
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
              mb={1}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ flex: 1, mr: 2 }}
              >
                {post.title}
              </Typography>
              <Box
                display="flex"
                gap={1}
                flexDirection="column"
                alignItems="flex-end"
              >
                {post.isForSale && (
                  <Chip
                    icon={<MoneyIcon />}
                    label={formatPrice(post.price || 0, post.currency || "USD")}
                    size="small"
                    color="secondary"
                    variant="filled"
                  />
                )}
                <Chip
                  icon={post.isForSale ? <LockIcon /> : <PublicIcon />}
                  label={post.isForSale ? "Premium" : "Free"}
                  size="small"
                  color={post.isForSale ? "secondary" : "primary"}
                  variant="outlined"
                />
              </Box>
            </Box>

            {/* Short Version/Preview */}
            {post.shortVersion && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontStyle: "italic",
                  lineHeight: 1.6,
                }}
              >
                {post.shortVersion}
              </Typography>
            )}

            {/* Preview of content for free posts, limited preview for paid posts */}
            {!post.isForSale ? (
              <Box sx={{ mb: 2 }}>
                <HtmlContent
                  content={
                    post.content.length > 300
                      ? post.content.substring(0, 300) + "..."
                      : post.content
                  }
                  className="post-content-preview"
                />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    backgroundColor: "action.hover",
                    p: 2,
                    borderRadius: 1,
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <LockIcon
                    sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }}
                  />
                  This is a premium article. Purchase to read the full content.
                  {post.shortVersion && post.shortVersion.length > 150 && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          fontStyle: "italic",
                          color: "text.secondary",
                        }}
                      >
                        Preview:
                      </Typography>
                      <HtmlContent
                        content={
                          post.shortVersion.length > 300
                            ? post.shortVersion.substring(0, 300) + "..."
                            : post.shortVersion
                        }
                        className="post-content-preview"
                      />
                    </>
                  )}
                </Typography>
              </Box>
            )}

            <Box
              className="post-meta"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                By {post.creatorUserName || "Unknown"} •{" "}
                {formatDate(post.creationTime)}
              </Typography>
            </Box>
          </CardContent>

          <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
            {post.isForSale ? (
              <Button
                variant="contained"
                startIcon={<MoneyIcon />}
                onClick={() => handleBuyPost(post)}
                color="secondary"
                size="small"
              >
                Buy Article
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={<ViewIcon />}
                onClick={() => handleViewPost(post)}
                size="small"
              >
                Read Full Article
              </Button>
            )}
          </CardActions>
        </Card>
      ))}

      {publishedPosts.length === 0 && <NoPostsIllustration />}
    </Box>
  );
};

export default PostList;
